import React from "react";
import { AnimatePresence, motion } from "framer-motion";
import { BedDouble, Check, Inbox, Mail, MessageSquare, QrCode, Target, UserRound } from "lucide-react";

export const fadeUp = (d = 0) => ({ initial: { opacity: 0, y: 10 }, animate: { opacity: 1, y: 0 }, transition: { delay: d, duration: 0.45 } });

export function CardFrame({ id, n, title, Icon, active, children, prefix = "exchange-intake-card" }) {
  return (
    <article data-testid={`${prefix}-${id}`}
      className={`rounded-2xl border bg-[#120C1C]/80 backdrop-blur-md p-5 transition-[border-color,box-shadow] duration-300 ${active ? "border-[#D4AF37]/70 shadow-[0_0_28px_rgba(212,175,55,0.18)]" : "border-[#D4AF37]/20"}`}>
      <div className="flex items-start gap-3 mb-4">
        <div className="w-10 h-10 shrink-0 rounded-xl bg-[#D4AF37]/12 border border-[#D4AF37]/30 flex items-center justify-center">
          <Icon className="w-5 h-5 text-[#F4D88A]" />
        </div>
        <div className="min-w-0">
          <div className="font-mono text-[10px] tracking-[0.2em] uppercase text-[#C9A227]">Card 0{n}</div>
          <h3 className="text-white font-semibold leading-snug">{title}</h3>
        </div>
      </div>
      {children}
    </article>
  );
}

const APPLICANTS = [
  { name: "Dana R.", housing: "Couch-surfing", ago: "12m" },
  { name: "Luis M.", housing: "Emergency shelter", ago: "8m" },
  { name: "Marcus T.", housing: "Unsheltered", ago: "2m", urgent: true },
];

export function IntakeQueueCard({ run, active }) {
  const [sorted, setSorted] = React.useState(true);
  React.useEffect(() => {
    if (!run) return undefined;
    setSorted(false);
    const t = setTimeout(() => setSorted(true), 1600);
    return () => clearTimeout(t);
  }, [run]);
  const rows = sorted ? [...APPLICANTS].sort((a, b) => (b.urgent ? 1 : 0) - (a.urgent ? 1 : 0)) : APPLICANTS;
  return (
    <CardFrame id="queue" n={1} title="Intake Queue & Triage" Icon={Inbox} active={active}>
      <ul className="space-y-2" key={run}>
        {rows.map((a, i) => (
          <motion.li layout key={a.name} {...fadeUp(i * 0.2)} data-testid={`exchange-intake-applicant-${i}`}
            className={`flex items-center gap-2 rounded-lg border px-3 py-2 text-sm ${a.urgent && sorted ? "border-amber-400/50 bg-amber-500/10" : "border-white/5 bg-white/[0.03]"}`}>
            <span className="text-white font-medium">{a.name}</span>
            <span className="text-slate-400 text-xs truncate">· {a.housing}</span>
            {a.urgent && <span className="ml-auto shrink-0 px-2 py-0.5 rounded-full bg-amber-500/20 text-amber-300 font-mono text-[9px] tracking-[0.16em]">URGENT</span>}
            <span className={`${a.urgent ? "" : "ml-auto"} shrink-0 font-mono text-[10px] ${sorted ? "text-slate-500" : "text-[#F4D88A]"}`}>{sorted ? "SEEN" : "NEW"} · {a.ago}</span>
          </motion.li>
        ))}
      </ul>
    </CardFrame>
  );
}

const PROFILE = [["Mobile", "(330) 555-0142"], ["Date of birth", "Mar 3, 1989"], ["Emergency contact", "Rita T. · sister"], ["Referring agency", "County Re-Entry Office"]];

export function ProfileBedCard({ run, active }) {
  return (
    <CardFrame id="profile" n={2} title="Resident Profile & Bed Assignment" Icon={UserRound} active={active}>
      <dl key={run} className="grid grid-cols-2 gap-2 text-xs">
        {PROFILE.map(([k, v], i) => (
          <motion.div key={k} {...fadeUp(0.15 + i * 0.25)} className="rounded-md bg-white/[0.03] border border-white/5 px-2.5 py-1.5">
            <dt className="text-slate-500 text-[10px] uppercase tracking-wider">{k}</dt>
            <dd className="text-white truncate">{v}</dd>
          </motion.div>
        ))}
        <motion.div {...fadeUp(1.4)} data-testid="exchange-intake-bed" className="col-span-2 flex items-center gap-2 rounded-lg border border-emerald-400/40 bg-emerald-500/10 px-3 py-2">
          <BedDouble className="w-4 h-4 text-emerald-300 shrink-0" />
          <span className="text-white text-xs">House 2 · Room 4 · Bed B · Mgr J. Carter · Move-in Oct 14</span>
          <Check className="w-4 h-4 text-emerald-300 ml-auto shrink-0" />
        </motion.div>
      </dl>
    </CardFrame>
  );
}

const AREAS = [["Housing", 2, 9], ["Employment", 3, 8], ["Spiritual", 4, 9]];

export function VisionBaselineCard({ run, active }) {
  return (
    <CardFrame id="c2c" n={3} title="C2C Vision Baseline" Icon={Target} active={active}>
      <div key={run} className="space-y-2.5">
        {AREAS.map(([area, today, goal], i) => (
          <div key={area} className="text-xs">
            <div className="flex justify-between text-slate-400 mb-1"><span>{area}</span><span className="font-mono text-[#F4D88A]">{today} → {goal}</span></div>
            <div className="relative h-2 rounded-full bg-white/5 overflow-hidden">
              <motion.div className="absolute inset-y-0 left-0 rounded-full bg-[#F4D88A]/25" initial={{ width: 0 }} animate={{ width: `${goal * 10}%` }} transition={{ delay: 0.5 + i * 0.2, duration: 0.9 }} />
              <motion.div className="absolute inset-y-0 left-0 rounded-full bg-gradient-to-r from-[#C9A227] to-[#F4D88A]" initial={{ width: 0 }} animate={{ width: `${today * 10}%` }} transition={{ delay: 0.2 + i * 0.2, duration: 0.6 }} />
            </div>
          </div>
        ))}
        <motion.p {...fadeUp(1.3)} className="text-sm italic text-slate-200">"My own apartment and a steady job by summer."</motion.p>
        <motion.span {...fadeUp(1.8)} data-testid="exchange-intake-goals" className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-emerald-500/15 border border-emerald-400/40 text-emerald-300 font-mono text-[10px] tracking-[0.14em] uppercase">
          <Check className="w-3 h-3" /> Goals generated
        </motion.span>
      </div>
    </CardFrame>
  );
}

const SPRINT = ["Right to work", "Sobriety", "Drug screening", "Program compliance", "Mental health"];

export function LifePassCard({ run, active }) {
  return (
    <CardFrame id="lifepass" n={4} title="Life Pass Activation" Icon={QrCode} active={active}>
      <div key={run} className="grid grid-cols-[150px_1fr] gap-3">
        <motion.div initial={{ rotateY: 90, opacity: 0 }} animate={{ rotateY: 0, opacity: 1 }} transition={{ duration: 0.7 }}
          data-testid="exchange-intake-lifepass"
          className="rounded-xl p-3 bg-gradient-to-br from-[#C9A227] via-[#F4D88A] to-[#C9A227] text-[#0A0612] shadow-[0_0_24px_rgba(212,175,55,0.35)]">
          <div className="font-mono text-[8px] tracking-[0.2em] font-bold">UCMS · LIFE PASS</div>
          <div className="mt-2 font-bold text-sm">Marcus T.</div>
          <div className="font-mono text-[9px]">ID TEA-04821</div>
          <div className="mt-2 flex items-end justify-between">
            <QrCode className="w-9 h-9" />
            <span className="px-1.5 py-0.5 rounded bg-[#0A0612] text-[#F4D88A] font-mono text-[8px] tracking-[0.14em]">ACTIVE</span>
          </div>
        </motion.div>
        <ul className="space-y-1 text-xs">
          <li className="font-mono text-[9px] tracking-[0.16em] uppercase text-[#C9A227] mb-1">Sprint Phase 1 · Foundations</li>
          {SPRINT.map((t, i) => (
            <motion.li key={t} {...fadeUp(0.6 + i * 0.22)} className="flex items-center gap-1.5 text-slate-200">
              <Check className="w-3.5 h-3.5 text-emerald-400 shrink-0" />{t}
            </motion.li>
          ))}
        </ul>
      </div>
      <AnimatePresence>
        <motion.div key={`alert-${run}`} {...fadeUp(1.9)} data-testid="exchange-intake-alert"
          className="mt-3 inline-flex items-center gap-2 px-3 py-1.5 rounded-lg bg-[#D4AF37]/15 border border-[#D4AF37]/40 text-[#F4D88A] text-xs">
          <MessageSquare className="w-3.5 h-3.5" /><Mail className="w-3.5 h-3.5" /> Care team alerted · SMS + Email
        </motion.div>
      </AnimatePresence>
    </CardFrame>
  );
}
